/**
 * CRA3D Landing — the public storefront.
 *
 * 2026-02 refinement (Mark's directive, this session):
 *   - Positioning: "Your experience still matters. Build forward from it."
 *     Plain explanation that CRA3D is an AI-assisted career assessment
 *     that uses the person's answers to suggest possible lanes of
 *     direction. No "scientifically validated"/"evidence-based"/
 *     guaranteed-employment claims.
 *   - "Lanes of direction" used consistently (replaces "evidence-linked
 *     lanes of direction").
 *   - Shortened page. Assessment offer is the main focus.
 *   - Reordered: Hero → How it works → Free vs Complete → Offers
 *     (inclusions + one-time price) → Founder / trust (Mark Dean,
 *     The Dean Offices LLC) → FAQ → Guided AI Learning (brief, below
 *     the core offer, preserves working waitlist) → Closing CTA.
 *   - Signed-in paid users continue to see "Continue Complete"; the
 *     entitlement-loading price-flash protection (pending variant) is
 *     preserved as-is.
 *   - Footer identity line now reads from BRAND.footer_identity (the
 *     approved "The Dean Offices LLC. CRA3D™ is a product of The Dean
 *     Offices LLC." wording) and the preview notice is rendered only
 *     when `isPreviewEnv()` is true — production builds carry no
 *     preview label.
 *   - Hero image loading flash fixed: eager fetch with high priority,
 *     a solid design-token background placeholder + skeleton pulse
 *     until the real image's `onLoad` fires. No new external assets.
 *   - All CTA destinations preserved exactly as previously approved.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Wordmark } from "@/components/Wordmark";
import { BRAND } from "@/config/brand";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/auth";
import { isPreviewEnv } from "@/lib/env";
import api from "@/lib/api";
import {
  ArrowRight, ShieldCheck, Compass, FileDown, Save, ListChecks,
  CheckCircle2, XCircle, ChevronDown, User,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1622151834625-66296f9f0e96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxhZHVsdCUyMHRob3VnaHRmdWwlMjB3b3JraW5nfGVufDB8fHx8MTc4NzkxMDg2NHww&ixlib=rb-4.1.0&q=85";

// Public policy version resolver — no auth required, silent fallback.
function usePolicyVersion(policyType) {
  const [version, setVersion] = useState("");
  useEffect(() => {
    let cancelled = false;
    const base = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${base}/api/policies/${policyType}`)
      .then((r) => { if (!cancelled) setVersion(r.data?.version || ""); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [policyType]);
  return version;
}

function formatUSD(cents) {
  if (cents == null) return null;
  if (cents === 0) return "$0";
  return `$${(cents / 100).toFixed(2)}`;
}

function HeaderNav({ user }) {
  // Concise top-nav. "Guided AI Learning" is intentionally omitted
  // because the section below is a single brief paragraph + waitlist
  // (per the 2026-02 landing refinement) — keeping a dedicated nav
  // link would overstate it.
  const items = [
    { href: "#how-it-works", label: "How it works", testid: "nav-how-it-works" },
    { href: "#free-vs-complete", label: "Free vs. Complete", testid: "nav-free-vs-complete" },
    { href: "#offers", label: "Offer", testid: "nav-offers" },
    { href: "#faq", label: "FAQ", testid: "nav-faq" },
  ];
  return (
    <header className="border-b border-border-subtle bg-bg-surface">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 min-h-16 py-2 flex items-center justify-between gap-2">
        <Link to="/" data-testid="landing-header-brand-link" className="min-w-0 flex-shrink-0">
          <Wordmark size="sm" testid="landing-header-wordmark" />
        </Link>
        <nav
          data-testid="landing-nav"
          className="hidden md:flex items-center gap-1 flex-shrink-0"
        >
          {items.map((it) => (
            <a
              key={it.testid}
              href={it.href}
              data-testid={it.testid}
              className="whitespace-nowrap text-sm px-3 py-2 rounded-md text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover transition-colors duration-200"
            >
              {it.label}
            </a>
          ))}
          {user ? (
            <Link
              to="/dashboard"
              data-testid="landing-nav-dashboard"
              className="whitespace-nowrap text-sm px-3 py-2 rounded-md text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover transition-colors duration-200"
            >
              Dashboard
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="landing-nav-login"
              className="whitespace-nowrap text-sm px-3 py-2 rounded-md text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover transition-colors duration-200"
            >
              Log in
            </Link>
          )}
        </nav>
        <div className="flex md:hidden items-center gap-2 flex-shrink-0">
          {user ? (
            <Link to="/dashboard" data-testid="landing-nav-dashboard-mobile">
              <Button size="sm" variant="outline" className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover">
                Dashboard
              </Button>
            </Link>
          ) : (
            <Link to="/login" data-testid="landing-nav-login-mobile">
              <Button size="sm" variant="outline" className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover">
                Log in
              </Button>
            </Link>
          )}
        </div>
      </div>
      {/* Mobile section anchors — compact strip so the four primary
          sections are reachable on small screens without wrapping. */}
      <div className="md:hidden border-t border-border-subtle bg-bg-surface">
        <div className="mx-auto max-w-6xl px-3 py-2 flex items-center gap-1 overflow-x-auto whitespace-nowrap">
          <a href="#how-it-works" data-testid="mnav-how-it-works" className="text-xs px-3 py-1.5 rounded-md text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">How it works</a>
          <a href="#free-vs-complete" data-testid="mnav-free-vs-complete" className="text-xs px-3 py-1.5 rounded-md text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">Free vs. Complete</a>
          <a href="#offers" data-testid="mnav-offers" className="text-xs px-3 py-1.5 rounded-md text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">Offer</a>
          <a href="#faq" data-testid="mnav-faq" className="text-xs px-3 py-1.5 rounded-md text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">FAQ</a>
        </div>
      </div>
    </header>
  );
}

function HowItWorksStep({ num, title, body, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-lg border border-border-subtle bg-bg-surface p-6"
    >
      <div className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-accent-warm/15 text-accent-warm font-heading text-sm"
        >
          {num}
        </span>
        <h3 className="text-lg font-heading font-medium text-fg-primary">{title}</h3>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-fg-secondary">{body}</p>
    </div>
  );
}

/* Round-27 Landing training purchase box — identical visual
 * vocabulary to the /training PurchaseStrip's PurchaseBox so the
 * two surfaces look like one coherent offer family. The annual
 * variant (tone="prominent") is the YELLOW dominant tile with the
 * owner's exact four Round-26 promotional lines. */
function LandingTrainingBox({ testid, label, price, includes,
                              onClick, tone, promo }) {
  const prominent = tone === "prominent";
  return (
    <div
      data-testid={testid}
      className={
        "rounded-lg border p-4 sm:p-5 flex flex-col gap-3 " +
        (prominent
          ? "border-accent-warm bg-accent-warm/10 shadow-sm"
          : "border-border-subtle bg-bg-surface")
      }
    >
      <p
        data-testid={`${testid}-label`}
        className={
          "text-xs uppercase tracking-widest " +
          (prominent ? "text-accent-warm" : "text-fg-secondary")
        }
      >
        {label}
      </p>
      {promo ? (
        <div data-testid={`${testid}-promo`} className="space-y-2">
          <p
            data-testid={`${testid}-promo-headline`}
            className="text-xl sm:text-2xl font-heading font-medium text-fg-primary leading-snug"
          >
            {promo.headline}
          </p>
          <ul
            data-testid={`${testid}-promo-lines`}
            className="space-y-1 text-sm text-fg-primary"
          >
            {promo.lines.map((line, i) => (
              <li key={i} data-testid={`${testid}-promo-line-${i}`}>
                {i === 0 ? (
                  <strong className="text-accent-warm">{line}</strong>
                ) : (
                  line
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="space-y-1">
          <p
            data-testid={`${testid}-price`}
            className="text-2xl font-heading font-medium text-fg-primary leading-tight"
          >
            {price}
          </p>
          <p
            data-testid={`${testid}-includes`}
            className="text-xs text-fg-secondary"
          >
            Includes {includes}
          </p>
        </div>
      )}
      <div className="mt-auto">
        <Button
          data-testid={`${testid}-cta`}
          onClick={onClick}
          size={prominent ? "lg" : "default"}
          className={
            "w-full " +
            (prominent
              ? "h-12 text-base bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
              : "bg-bg-surface-hover text-fg-primary hover:bg-bg-surface")
          }
        >
          {prominent
            ? `Join the 12-month program \u2014 ${price}`
            : `Buy ${label.toLowerCase()}`}
          <ArrowRight className="h-3.5 w-3.5 ml-2" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}


function ComparisonRow({ testid, label, freeText, completeText }) {
  return (
    <div
      data-testid={testid}
      className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 py-4 border-b border-border-subtle last:border-b-0"
    >
      <div className="text-sm font-medium text-fg-primary">{label}</div>
      <div className="text-sm text-fg-secondary leading-relaxed">
        <span className="sm:hidden text-fg-muted text-xs uppercase tracking-wider mr-2">Free:</span>
        {freeText}
      </div>
      <div className="text-sm text-fg-primary leading-relaxed">
        <span className="sm:hidden text-accent-warm text-xs uppercase tracking-wider mr-2">Complete:</span>
        {completeText}
      </div>
    </div>
  );
}

function FaqItem({ testid, q, a, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      data-testid={testid}
      className="border-b border-border-subtle last:border-b-0"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`${testid}-toggle`}
        className="w-full flex items-center justify-between gap-3 py-4 text-left"
      >
        <span className="text-base font-medium text-fg-primary">{q}</span>
        <ChevronDown
          className={`h-4 w-4 text-fg-secondary flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div
          data-testid={`${testid}-answer`}
          className="pb-4 pr-6 text-sm leading-relaxed text-fg-secondary"
        >
          {a}
        </div>
      )}
    </div>
  );
}

export default function Landing() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const privacyVersion = usePolicyVersion("privacy");
  const termsVersion = usePolicyVersion("terms");
  const [plans, setPlans] = useState(null);
  // See earlier commentary: pending variant until BOTH auth + access
  // settle, so no wrong-state actionable CTA ever paints and no price
  // flash occurs during entitlement load.
  const [access, setAccess] = useState(null);
  const [accessLoaded, setAccessLoaded] = useState(false);
  const [laneInfo, setLaneInfo] = useState(null);
  // Hero image load tracking for the flash fix: the placeholder
  // (solid design-token background + skeleton pulse) stays in place
  // until `onLoad` fires so the first paint never shows a broken
  // or half-rendered image frame.
  const [heroImgLoaded, setHeroImgLoaded] = useState(false);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => setPlans(null));
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setAccess(null);
      setAccessLoaded(true);
      setLaneInfo(null);
      return;
    }
    let cancelled = false;
    setAccessLoaded(false);
    api.get("/me/access")
      .then((r) => { if (!cancelled) { setAccess(r.data); setAccessLoaded(true); } })
      .catch(() => { if (!cancelled) { setAccess(null); setAccessLoaded(true); } });
    api.get("/assessment/lanes")
      .then((r) => { if (!cancelled) setLaneInfo(r.data); })
      .catch(() => { if (!cancelled) setLaneInfo(null); });
    return () => { cancelled = true; };
  }, [user, authLoading]);

  const hasCompleteAccess = !!access?.capabilities?.complete_assessment_access;
  const variant = authLoading
    ? "pending"
    : (!user
        ? "signed-out"
        : (!accessLoaded
            ? "pending"
            : (hasCompleteAccess ? "signed-in-paid" : "signed-in-unpaid")));

  const completeProduct = useMemo(
    () => (plans?.products || []).find((p) => p.slug === "three_directions"),
    [plans],
  );
  const completeCents = (completeProduct?.draft_prices?.one_time || {}).USD;
  const priceLabel = formatUSD(completeCents) || "$24.99";

  // Continue-Complete destination for signed-in paid users — mirrors
  // Dashboard "Continue Complete Career Repath Assessment" routing.
  const continueDest = (() => {
    const currentLane = laneInfo?.current_lane;
    const completedLane = laneInfo?.completed_lane;
    if (currentLane === "complete" && completedLane === "complete") {
      return "/results/complete";
    }
    if (currentLane) return "/assessment";
    return "/assessment";
  })();

  const goPaidCta = () => {
    if (variant === "pending") return;
    if (variant === "signed-out") {
      navigate("/register?intent_slug=three_directions");
    } else if (variant === "signed-in-unpaid") {
      navigate("/plans?intent_slug=three_directions");
    } else {
      navigate(continueDest);
    }
  };
  const goFreeCta = () => {
    if (variant === "pending") return;
    if (variant === "signed-out") {
      navigate("/register?intent_slug=free_explorer");
    } else if (variant === "signed-in-unpaid") {
      navigate("/assessment/choose");
    } else {
      navigate("/dashboard");
    }
  };

  const primaryCtaLabel = variant === "pending"
    ? "Loading…"
    : (variant === "signed-in-paid"
        ? "Continue Complete Career Repath Assessment"
        : `Unlock complete assessment — ${priceLabel}`);
  const secondaryCtaLabel = variant === "pending"
    ? "Loading…"
    : (variant === "signed-in-paid" ? "Go to Dashboard" : "Start free — 18 questions");
  const ctaDisabled = variant === "pending";
  const stateResolved = variant !== "pending";

  return (
    <div className="min-h-screen bg-bg-base text-fg-primary">
      <a href="#main-content" className="cra3d-skip-link" data-testid="skip-to-main">
        Skip to main content
      </a>
      <HeaderNav user={user} />

      <main id="main-content" tabIndex={-1}>
        {/* 1. HERO — headline, short explanation, primary CTA. */}
        <section className="relative overflow-hidden">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="mb-4">
                <Wordmark size="lg" testid="landing-hero-wordmark" />
              </div>
              <p
                data-testid="landing-hero-tagline"
                className="text-sm sm:text-base uppercase tracking-widest text-accent-warm"
              >
                {BRAND.tagline}
              </p>
              <h1
                data-testid="landing-hero-headline"
                className="mt-3 text-4xl sm:text-5xl lg:text-6xl tracking-tight font-heading font-medium text-fg-primary leading-tight"
              >
                Your experience still matters.
                <span className="block text-fg-secondary">Build forward from it.</span>
              </h1>
              <p
                data-testid="landing-hero-support"
                className="mt-5 text-base sm:text-lg leading-relaxed text-fg-secondary max-w-2xl"
              >
                CRA3D is an AI-assisted career assessment that uses your own
                answers to suggest possible lanes of direction. Start with a
                free 18-question snapshot, or unlock the complete career
                assessment for a single payment.
              </p>
              <div
                data-testid="landing-hero-ctas"
                className="mt-8 flex flex-col sm:flex-row gap-3"
              >
                <Button
                  size="lg"
                  onClick={goPaidCta}
                  disabled={ctaDisabled}
                  aria-busy={ctaDisabled}
                  data-testid="landing-cta-complete"
                  data-variant={variant}
                  className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover w-full sm:w-auto disabled:opacity-60 disabled:cursor-progress"
                >
                  {primaryCtaLabel}
                  {stateResolved && (
                    <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
                  )}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={goFreeCta}
                  disabled={ctaDisabled}
                  aria-busy={ctaDisabled}
                  data-testid="landing-cta-free"
                  data-variant={variant}
                  className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover w-full sm:w-auto disabled:opacity-60 disabled:cursor-progress"
                >
                  {secondaryCtaLabel}
                </Button>
              </div>
              {(variant === "signed-out" || variant === "signed-in-unpaid") && (
                <div
                  data-testid="landing-hero-support-labels"
                  className="mt-4 flex flex-col sm:flex-row gap-x-6 gap-y-1 text-xs sm:text-sm text-fg-secondary"
                >
                  <span data-testid="landing-hero-complete-label">
                    <strong className="text-fg-primary">Complete:</strong> one-time payment, no subscription.
                  </span>
                  <span data-testid="landing-hero-free-label">
                    <strong className="text-fg-primary">Free:</strong> no card required.
                  </span>
                </div>
              )}
              {variant === "signed-in-paid" && (
                <p
                  data-testid="landing-hero-paid-notice"
                  className="mt-4 text-xs sm:text-sm text-fg-secondary"
                >
                  You have Complete Career Repath access. Pick up where you
                  left off, or head to your dashboard.
                </p>
              )}
            </div>
            <div className="lg:col-span-5">
              {/* Hero image flash fix: the wrapper uses a solid design-token
                  background and reserves the final image dimensions so the
                  layout never shifts. The <img> loads eagerly with high
                  fetchpriority, and a skeleton pulse covers the frame
                  until onLoad fires. */}
              <div
                data-testid="landing-hero-image"
                className="relative rounded-lg overflow-hidden border border-border-subtle bg-bg-surface"
                style={{ backgroundColor: "var(--cra3d-bg-surface)" }}
              >
                {!heroImgLoaded && (
                  <div
                    aria-hidden="true"
                    data-testid="landing-hero-image-skeleton"
                    className="absolute inset-0 bg-bg-surface-hover animate-pulse"
                  />
                )}
                <img
                  src={HERO_IMG}
                  alt="Adult working thoughtfully at a laptop in a calm workspace"
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                  onLoad={() => setHeroImgLoaded(true)}
                  onError={() => setHeroImgLoaded(true)}
                  className={`w-full h-[280px] sm:h-[360px] object-cover relative transition-opacity duration-300 ${heroImgLoaded ? "opacity-100" : "opacity-0"}`}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Round-27 — Independent AI Training purchase section on
            the front page. Sits directly below the Complete Career
            Repath Assessment hero so the two independent offers
            (Assessment $24.99 one-time + AI Training packages) are
            both visible above the fold / near-the-top. All four
            tiles route to /plans so the preserved Stripe TEST
            hosted-checkout flow, upgrade overlays, and consent
            disclosures apply exactly as they do on /plans itself.
            The annual tile stays the dominant YELLOW offer and
            carries the owner's exact Round-26 four promotional
            lines. Visible to every landing visitor regardless of
            auth state — purchasing works for anonymous visitors
            (routes them through registration first), as it does on
            /plans. */}
        <section
          id="training-offers"
          aria-labelledby="landing-training-offers-heading"
          className="border-t border-border-subtle bg-bg-base"
          data-testid="landing-training-offers"
        >
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
            <p
              data-testid="landing-training-offers-eyebrow"
              className="text-[11px] uppercase tracking-widest text-fg-secondary"
            >
              AI Training &middot; a separate, independent offer
            </p>
            <h2
              id="landing-training-offers-heading"
              data-testid="landing-training-offers-heading"
              className="mt-1 text-2xl sm:text-3xl tracking-tight font-heading font-medium text-fg-primary"
            >
              Build practical AI skills, month by month.
            </h2>
            <p
              className="mt-3 max-w-2xl text-sm text-fg-secondary leading-relaxed"
            >
              Four purchase options. Buy a single month at a time, pay
              once for the full twelve-month program, or anywhere in
              between. The Complete Career Repath Assessment above is
              not required and not bundled with any of these packages.
            </p>
            <div
              data-testid="landing-training-offers-grid"
              className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
            >
              <LandingTrainingBox
                testid="landing-training-30d"
                label="30-day package"
                price="$29.99"
                includes="Month 1"
                onClick={() => navigate("/plans?intent_slug=training_30d")}
              />
              <LandingTrainingBox
                testid="landing-training-60d"
                label="60-day package"
                price="$49.99"
                includes={"Months 1\u20132"}
                onClick={() => navigate("/plans?intent_slug=training_60d")}
              />
              <LandingTrainingBox
                testid="landing-training-90d"
                label="90-day package"
                price="$69.99"
                includes={"Months 1\u20133"}
                onClick={() => navigate("/plans?intent_slug=training_90d")}
              />
              <div className="sm:col-span-2 lg:col-span-4">
                <LandingTrainingBox
                  testid="landing-training-annual"
                  label="Annual membership"
                  price="$149.99"
                  includes="Access all 12 months of AI Training content."
                  onClick={() => navigate("/plans?intent_slug=training_membership_annual")}
                  tone="prominent"
                  promo={{
                    headline: "Get all 12 months of AI training.",
                    lines: [
                      "$149.99 for your first year.",
                      "Access every month immediately. Learn in any order.",
                      "Renews automatically at $169.99 per year.",
                    ],
                  }}
                />
              </div>
            </div>
            {/* Round-28 (2026-10-08): package-label clarification.
                "30/60/90-day" are suggested learning schedules only
                — access does not expire by elapsed days. No price,
                offer, or access-rule change. */}
            <p
              data-testid="landing-training-offers-label-note"
              className="mt-4 text-xs text-fg-secondary"
            >
              The &ldquo;30-day&rdquo;, &ldquo;60-day&rdquo;, and
              &ldquo;90-day&rdquo; names above describe a suggested
              learning schedule. Months you have unlocked stay
              accessible for the full 12-month access period after
              purchase &mdash; lessons never lock because days
              elapsed.
            </p>
            <p
              data-testid="landing-training-offers-free-note"
              className="mt-4 text-xs text-fg-secondary"
            >
              Not ready to buy?{" "}
              <Link
                to="/plans"
                className="underline hover:text-fg-primary"
                data-testid="landing-training-offers-plans-link"
              >
                See the full Plans &amp; Pricing page
              </Link>
              {" "}&mdash; free{" "}
              <strong className="text-fg-primary">Start Here</strong> lessons
              on digital basics are available after you register and remain a
              secondary option.
            </p>
          </div>
        </section>
        <section
          id="how-it-works"
          aria-labelledby="how-it-works-heading"
          className="border-t border-border-subtle bg-bg-surface"
        >
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
            <h2
              id="how-it-works-heading"
              data-testid="landing-how-it-works-heading"
              className="text-2xl sm:text-3xl tracking-tight font-heading font-medium text-fg-primary"
            >
              How it works
            </h2>
            <div
              data-testid="landing-how-it-works-steps"
              className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4"
            >
              <HowItWorksStep
                testid="landing-how-step-1"
                num="1"
                title="Share your experience"
                body="Answer in your own words about real work and life moments. No aptitude quizzes, no scoring, no timers. Answers save automatically."
              />
              <HowItWorksStep
                testid="landing-how-step-2"
                num="2"
                title="See your lanes of direction"
                body="Read a preliminary snapshot for free, or go deeper with the complete assessment for three suggested lanes of direction grounded in your own answers."
              />
              <HowItWorksStep
                testid="landing-how-step-3"
                num="3"
                title="Choose what fits — plan your next step"
                body="Accept, reject, or reconsider any direction. Nothing is locked in. Your data, your call."
              />
            </div>
          </div>
        </section>

        {/* 3. FREE VS COMPLETE — honest comparison. */}
        <section
          id="free-vs-complete"
          aria-labelledby="free-vs-complete-heading"
          className="border-t border-border-subtle bg-bg-base"
        >
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
            <h2
              id="free-vs-complete-heading"
              data-testid="landing-compare-heading"
              className="text-2xl sm:text-3xl tracking-tight font-heading font-medium text-fg-primary"
            >
              Free vs. Complete &mdash; the honest difference
            </h2>
            <p className="mt-3 max-w-2xl text-sm text-fg-secondary leading-relaxed">
              Plain-text comparison. No unexplained checkmarks.
            </p>

            <div
              data-testid="landing-compare-table"
              className="mt-8 rounded-lg border border-border-subtle bg-bg-surface px-4 sm:px-6"
            >
              <div className="hidden sm:grid grid-cols-3 gap-4 py-3 border-b border-border-subtle text-xs uppercase tracking-widest text-fg-secondary">
                <div>What&rsquo;s covered</div>
                <div>Free &mdash; Quick Repath</div>
                <div className="text-accent-warm">
                  {variant === "pending" ? (
                    <span
                      data-testid="landing-compare-header-complete-skeleton"
                      aria-busy="true"
                      aria-label="Loading"
                      className="inline-block h-4 w-40 rounded bg-bg-surface-hover animate-pulse align-middle"
                    />
                  ) : variant === "signed-in-paid" ? (
                    "Complete"
                  ) : (
                    <>Complete &mdash; {priceLabel}</>
                  )}
                </div>
              </div>
              <ComparisonRow
                testid="landing-compare-questions"
                label="Questions"
                freeText="18 questions"
                completeText="Full 56 questions"
              />
              <ComparisonRow
                testid="landing-compare-analysis"
                label="Analysis"
                freeText="Preliminary snapshot"
                completeText="Complete analysis + three suggested lanes of direction"
              />
              <ComparisonRow
                testid="landing-compare-pdf"
                label="PDF report"
                freeText="Preliminary snapshot PDF"
                completeText="Full assessment PDF"
              />
              <ComparisonRow
                testid="landing-compare-export"
                label="Data export"
                freeText="Account-data JSON export (your answers + consents, from Settings)"
                completeText="Full-analysis JSON export (directions + answers + profile)"
              />
              <ComparisonRow
                testid="landing-compare-action-plan"
                label="Action plan"
                freeText="Not included"
                completeText="Initial action-plan outline"
              />
              <ComparisonRow
                testid="landing-compare-reconsiderations"
                label="Direction reconsiderations"
                freeText="Not included"
                completeText="10 (lifetime allowance, not a daily limit)"
              />
              <ComparisonRow
                testid="landing-compare-carryover"
                label="Upgrade path"
                freeText="Answers auto-carry into the Complete assessment"
                completeText="Complete answers stay yours; no re-entry needed"
              />
            </div>
          </div>
        </section>

        {/* 4. OFFERS — verified inclusions + one-time price. Hidden
            for signed-in paid users (no duplicate-purchase messaging);
            replaced with a compact "you have access" banner. */}
        <section
          id="offers"
          aria-labelledby="offers-heading"
          className="border-t border-border-subtle bg-bg-surface"
        >
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
            {variant === "pending" ? (
              <div
                data-testid="landing-offers-skeleton"
                aria-busy="true"
                aria-label="Loading options"
                className="rounded-lg border border-border-subtle bg-bg-base p-6 text-sm text-fg-secondary"
              >
                Loading&hellip;
              </div>
            ) : variant === "signed-in-paid" ? (
              <div
                data-testid="landing-paid-access-banner"
                className="rounded-lg border border-progress-teal/30 bg-progress-teal/5 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
              >
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-progress-teal">
                    Complete Career Repath &middot; active
                  </p>
                  <h2
                    id="offers-heading"
                    data-testid="landing-paid-access-title"
                    className="mt-1 text-xl sm:text-2xl font-heading font-medium text-fg-primary"
                  >
                    You already have Complete Career Repath access.
                  </h2>
                  <p className="mt-2 text-sm text-fg-secondary max-w-2xl leading-relaxed">
                    Pick up your assessment where you left off, or open your
                    dashboard for the full workspace.
                  </p>
                </div>
                <div className="flex-shrink-0 flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={goPaidCta}
                    data-testid="landing-paid-access-continue"
                    className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                  >
                    Continue Complete Assessment
                    <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={goFreeCta}
                    data-testid="landing-paid-access-dashboard"
                    className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
                  >
                    Go to Dashboard
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <h2
                  id="offers-heading"
                  data-testid="landing-offers-heading"
                  className="text-2xl sm:text-3xl tracking-tight font-heading font-medium text-fg-primary"
                >
                  Two clear ways to begin.
                </h2>
                <p className="mt-3 max-w-2xl text-sm sm:text-base text-fg-secondary leading-relaxed">
                  Pick a path. Answers save automatically as you go, and matching
                  answers carry over losslessly if you decide to upgrade.
                </p>

                <div className="mt-8 grid gap-6 md:grid-cols-2" data-testid="landing-offer-cards">
                  {/* FREE */}
                  <div
                    data-testid="landing-offer-free"
                    className="relative rounded-lg border border-border-subtle bg-bg-base p-6 flex flex-col"
                  >
                    <p className="text-[11px] uppercase tracking-widest text-fg-secondary">
                      Free &middot; No card required
                    </p>
                    <h3 className="mt-1 text-xl font-heading text-fg-primary">Quick Repath</h3>
                    <p className="mt-3 text-3xl font-heading text-fg-primary leading-tight">
                      $0 <span className="text-sm text-fg-secondary ml-2">forever</span>
                    </p>
                    <p className="mt-3 text-sm text-fg-secondary leading-relaxed">
                      An 18-question path across the same six areas as the complete
                      assessment. Gives you a preliminary snapshot in your own words.
                    </p>
                    <ul className="mt-5 space-y-2 text-sm text-fg-primary flex-1" data-testid="landing-offer-free-inclusions">
                      <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" /><span>18-question assessment</span></li>
                      <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" /><span>Preliminary snapshot of results</span></li>
                      <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" /><span>Downloadable preliminary snapshot PDF</span></li>
                      <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" /><span>Existing answers carry forward if you upgrade</span></li>
                    </ul>
                    <div className="mt-6">
                      <Button
                        onClick={goFreeCta}
                        data-testid="landing-offer-free-cta"
                        className="w-full bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                      >
                        Start free — 18 questions
                        <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
                      </Button>
                    </div>
                  </div>

                  {/* COMPLETE */}
                  <div
                    data-testid="landing-offer-complete"
                    className="relative rounded-lg border border-accent-warm/40 bg-accent-warm/5 p-6 flex flex-col shadow-md"
                  >
                    <Badge
                      data-testid="landing-offer-complete-badge"
                      className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent-warm text-bg-base hover:bg-accent-warm uppercase text-[10px] tracking-widest px-3"
                    >
                      Complete Assessment
                    </Badge>
                    <p className="text-[11px] uppercase tracking-widest text-fg-secondary">
                      One-time &middot; No recurring charge
                    </p>
                    <h3 className="mt-1 text-xl font-heading text-fg-primary">
                      {completeProduct?.name || "Complete Career Repath Assessment"}
                    </h3>
                    <p className="mt-3 text-3xl font-heading text-fg-primary leading-tight">
                      {priceLabel}
                      <span className="text-sm text-fg-secondary ml-2">one-time</span>
                    </p>
                    <p className="mt-3 text-sm text-fg-secondary leading-relaxed">
                      Full 56 questions. Three suggested lanes of direction
                      grounded in your own answers &mdash; each AI-drafted
                      conclusion is correctable or rejectable by you.
                    </p>
                    <ul className="mt-5 space-y-2 text-sm text-fg-primary flex-1" data-testid="landing-offer-complete-inclusions">
                      <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" /><span>Full 56-question assessment</span></li>
                      <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" /><span>Three suggested lanes of direction</span></li>
                      <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" /><span>Full assessment PDF</span></li>
                      <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" /><span>JSON export of answers and analysis</span></li>
                      <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" /><span>Initial action-plan outline</span></li>
                      <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" /><span>10 direction reconsiderations (lifetime allowance)</span></li>
                      <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" /><span>Existing free answers carry forward without re-entry</span></li>
                      <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-progress-teal flex-shrink-0 mt-0.5" aria-hidden="true" /><span>No recurring assessment charge</span></li>
                    </ul>
                    <div className="mt-6">
                      <Button
                        onClick={goPaidCta}
                        data-testid="landing-offer-complete-cta"
                        className="w-full bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
                      >
                        Unlock complete assessment &mdash; {priceLabel}
                      </Button>
                      <p className="mt-2 text-xs text-fg-secondary text-center">
                        Card required. Refundable within 7 days if the report hasn&rsquo;t been used.
                      </p>
                    </div>
                  </div>
                </div>

                <p className="mt-6 text-xs text-fg-muted">
                  You can buy the complete assessment right away &mdash; the free
                  route is optional, not a prerequisite.
                </p>
              </>
            )}
          </div>
        </section>

        {/* 5. FOUNDER / TRUST — Mark Dean, The Dean Offices LLC + control cues. */}
        <section
          id="trust"
          aria-labelledby="trust-heading"
          className="border-t border-border-subtle bg-bg-base"
        >
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
            <div
              data-testid="landing-founder-block"
              className="rounded-lg border border-border-subtle bg-bg-surface p-6 sm:p-8"
            >
              <div className="flex items-start gap-4">
                <span
                  aria-hidden="true"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-accent-warm/15 text-accent-warm flex-shrink-0"
                >
                  <User className="h-5 w-5" />
                </span>
                <div>
                  <p
                    data-testid="landing-founder-label"
                    className="text-[11px] uppercase tracking-widest text-fg-secondary"
                  >
                    Founder &amp; company
                  </p>
                  <h2
                    id="trust-heading"
                    data-testid="landing-founder-heading"
                    className="mt-1 text-2xl sm:text-3xl tracking-tight font-heading font-medium text-fg-primary"
                  >
                    Built by <span data-testid="landing-founder-name">{BRAND.founder}</span>, a product of <span data-testid="landing-founder-company">{BRAND.company}</span>.
                  </h2>
                  <p className="mt-3 max-w-2xl text-sm sm:text-base text-fg-secondary leading-relaxed">
                    CRA3D is designed and operated by Mark Dean through
                    The Dean Offices LLC. We're a U.S.-based business and
                    looking to go global. Clear purchase terms,
                    direct support, and plain-language policies &mdash;
                    no third-party brokers.
                  </p>
                  <p
                    data-testid="landing-founder-support-line"
                    className="mt-3 text-sm text-fg-secondary leading-relaxed"
                  >
                    Need help with your purchase or your account?{" "}
                    <a
                      href="mailto:support@cra3d.com"
                      data-testid="landing-founder-support-link"
                      className="underline hover:text-fg-primary"
                    >
                      support@cra3d.com
                    </a>
                  </p>
                </div>
              </div>
            </div>

            <div
              data-testid="landing-trust-tiles"
              className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
            >
              <div data-testid="landing-trust-decide" className="rounded-lg border border-border-subtle bg-bg-surface p-5">
                <ListChecks className="h-5 w-5 text-accent-warm" aria-hidden="true" />
                <p className="mt-3 text-sm font-medium text-fg-primary">You decide, we suggest</p>
                <p className="mt-1 text-xs text-fg-secondary leading-relaxed">
                  Suggestions are possibilities you evaluate &mdash; not
                  personality diagnoses, not guaranteed placements.
                </p>
              </div>
              <div data-testid="landing-trust-save" className="rounded-lg border border-border-subtle bg-bg-surface p-5">
                <Save className="h-5 w-5 text-accent-warm" aria-hidden="true" />
                <p className="mt-3 text-sm font-medium text-fg-primary">Autosaves as you go</p>
                <p className="mt-1 text-xs text-fg-secondary leading-relaxed">
                  Answers save automatically. Close the tab, come back next
                  week, pick up where you left off.
                </p>
              </div>
              <div data-testid="landing-trust-export" className="rounded-lg border border-border-subtle bg-bg-surface p-5">
                <FileDown className="h-5 w-5 text-accent-warm" aria-hidden="true" />
                <p className="mt-3 text-sm font-medium text-fg-primary">Export what&rsquo;s yours</p>
                <p className="mt-1 text-xs text-fg-secondary leading-relaxed">
                  Free plan: download your account-data JSON any time from{" "}
                  <Link to="/login" className="underline hover:text-fg-primary">Settings</Link>. Complete plan adds the full analysis PDF and JSON.
                </p>
              </div>
              <div data-testid="landing-trust-delete" className="rounded-lg border border-border-subtle bg-bg-surface p-5">
                <XCircle className="h-5 w-5 text-accent-warm" aria-hidden="true" />
                <p className="mt-3 text-sm font-medium text-fg-primary">Delete when you&rsquo;re done</p>
                <p className="mt-1 text-xs text-fg-secondary leading-relaxed">
                  Settings &rarr; Delete account permanently removes your data.
                  Consent versions and policy history are kept for compliance.
                </p>
              </div>
            </div>

            <p className="mt-6 text-xs text-fg-muted max-w-3xl leading-relaxed">
              Note: saving your answers requires storing them on your account.
              We do not store raw prompts or narrative content in AI usage
              logs. Full detail lives in the{" "}
              <Link to="/privacy" className="underline hover:text-fg-primary" data-testid="landing-trust-privacy-link">Privacy Notice</Link>.
            </p>
          </div>
        </section>

        {/* 6. FAQ — concise. */}
        <section
          id="faq"
          aria-labelledby="faq-heading"
          className="border-t border-border-subtle bg-bg-surface"
        >
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
            <h2
              id="faq-heading"
              data-testid="landing-faq-heading"
              className="text-2xl sm:text-3xl tracking-tight font-heading font-medium text-fg-primary"
            >
              FAQ
            </h2>
            <div
              data-testid="landing-faq-list"
              className="mt-6 rounded-lg border border-border-subtle bg-bg-base px-4 sm:px-6"
            >
              <FaqItem
                testid="faq-what-free"
                defaultOpen
                q="What do I get for free?"
                a="The 18-question Quick Repath assessment, a preliminary snapshot of your interests, strengths and possible directions, a downloadable snapshot PDF, and an account-data JSON export of your answers and consents. No credit card is required."
              />
              <FaqItem
                testid="faq-what-24"
                q={
                  variant === "pending" ? (
                    <span
                      data-testid="faq-what-24-question-skeleton"
                      aria-busy="true"
                      aria-label="Loading"
                      className="inline-block h-4 w-72 max-w-full rounded bg-bg-surface-hover animate-pulse align-middle"
                    />
                  ) : variant === "signed-in-paid" ? (
                    "What does the Complete Career Repath Assessment include?"
                  ) : (
                    `What does ${priceLabel} unlock?`
                  )
                }
                a="The Complete Career Repath Assessment: 56 questions, a full nine-part profile, three suggested lanes of direction, skill-gap analysis per lane, an initial 30/60/90-day action-plan outline, 10 lifetime direction reconsiderations, and both the full PDF report and JSON export of answers + analysis."
              />
              <FaqItem
                testid="faq-subscription"
                q="Is it a subscription?"
                a="No. This assessment is a one-time purchase. There is no recurring charge for the assessment itself."
              />
              <FaqItem
                testid="faq-need-free-first"
                q="Do I need to finish the free assessment before buying?"
                a="No. You can buy the complete assessment right away and start from the full 56-question path."
              />
              <FaqItem
                testid="faq-repeat"
                q="Do I repeat my answers after upgrading?"
                a="No. Matching answers you gave in the Quick Repath carry over into the Complete Career Repath Assessment losslessly. Only the additional Complete questions still need answering."
              />
              <FaqItem
                testid="faq-save-return"
                q="Can I save and continue later?"
                a="Yes &mdash; answers save automatically as you go. Close the tab or the browser, come back later, and pick up on the same question."
              />
              <FaqItem
                testid="faq-does-cra3d-choose"
                q="Does CRA3D choose my career for me?"
                a="No. CRA3D suggests possible lanes of direction grounded in your own answers. You decide what to accept, reject, or reconsider. No employment or income guarantees, no accredited certification."
              />
              <FaqItem
                testid="faq-access-manage"
                q="How can I access and manage my information?"
                a="Once signed in, Settings gives you full profile view, password change, logout-all-sessions, consent history, JSON export of your account data, and permanent account deletion."
              />
              <FaqItem
                testid="faq-refund"
                q="What is the refund policy?"
                a={
                  <>
                    The Complete Career Repath Assessment is fully refundable
                    within 7 days if the report hasn&rsquo;t been used. Full
                    detail is in our{" "}
                    <Link to="/refund-policy" className="underline hover:text-fg-primary" data-testid="faq-refund-link">Refund &amp; Cancellation Policy</Link>.
                  </>
                }
              />
            </div>
          </div>
        </section>

        {/* 8. CLOSING CTA. */}
        <section
          aria-labelledby="closing-heading"
          className="border-t border-border-subtle bg-bg-surface"
        >
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-14 sm:py-16 text-center">
            <Compass className="h-8 w-8 text-accent-warm mx-auto" aria-hidden="true" />
            <h2
              id="closing-heading"
              data-testid="landing-closing-heading"
              className="mt-4 text-3xl sm:text-4xl tracking-tight font-heading font-medium text-fg-primary"
            >
              {variant === "signed-in-paid"
                ? "Ready to keep exploring your lanes of direction?"
                : "Ready to see your lanes of direction?"}
            </h2>
            <p className="mt-3 text-sm sm:text-base text-fg-secondary max-w-xl mx-auto leading-relaxed">
              {variant === "signed-in-paid"
                ? "Pick up your Complete assessment, or open your dashboard for the full workspace."
                : <>Pick whichever path fits today. You can always upgrade later &mdash; your answers travel with you.</>
              }
            </p>
            <div
              data-testid="landing-closing-ctas"
              className="mt-8 flex flex-col sm:flex-row gap-3 justify-center"
            >
              <Button
                size="lg"
                onClick={goPaidCta}
                disabled={ctaDisabled}
                aria-busy={ctaDisabled}
                data-testid="landing-closing-cta-complete"
                data-variant={variant}
                className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover w-full sm:w-auto disabled:opacity-60 disabled:cursor-progress"
              >
                {primaryCtaLabel}
                {stateResolved && (
                  <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
                )}
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={goFreeCta}
                disabled={ctaDisabled}
                aria-busy={ctaDisabled}
                data-testid="landing-closing-cta-free"
                data-variant={variant}
                className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover w-full sm:w-auto disabled:opacity-60 disabled:cursor-progress"
              >
                {secondaryCtaLabel}
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER — approved identity line. Preview notice is rendered
          conditionally so a production build never carries it. */}
      <footer className="border-t border-border-subtle bg-bg-surface">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <Wordmark size="sm" testid="landing-footer-wordmark" />
            <p
              className="mt-2 text-xs text-fg-secondary max-w-md"
              data-testid="landing-footer-ownership"
            >
              © {new Date().getFullYear()} {BRAND.footer_identity}
            </p>
            {isPreviewEnv() && (
              <p
                className="mt-1 text-xs text-fg-muted max-w-md"
                data-testid="landing-footer-preview-notice"
              >
                {BRAND.preview_notice}
              </p>
            )}
            <p
              className="mt-1 text-xs text-fg-muted max-w-md"
              data-testid="landing-footer-founder-line"
            >
              Founded by {BRAND.founder}.
            </p>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-fg-secondary">
            <Link to="/privacy" data-testid="landing-footer-privacy" className="hover:text-fg-primary transition-colors duration-200">
              Privacy{privacyVersion ? ` (v${privacyVersion})` : ""}
            </Link>
            <Link to="/terms" data-testid="landing-footer-terms" className="hover:text-fg-primary transition-colors duration-200">
              Terms{termsVersion ? ` (v${termsVersion})` : ""}
            </Link>
            <Link to="/refund-policy" data-testid="landing-footer-refund" className="hover:text-fg-primary transition-colors duration-200">
              Refund Policy
            </Link>
            <a href="mailto:support@cra3d.com" data-testid="landing-footer-support" className="hover:text-fg-primary transition-colors duration-200">
              Support
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
